import React, { useState } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import '../agent-training.css';
import { CancelButton, PrimaryButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #f8f9fa;
  border-left: 3px solid #6c757d;
  border-radius: 4px;
  margin-top: 0.75rem;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: #6c757d;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #495057;

    strong {
      font-weight: 600;
      color: #212529;
    }
  }
`;

const AddFilesModal = ({ show, onHide, workspaceId, apiUrl, onPollState }) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (!loading) {
      setFiles([]);
      setError(null);
      onHide();
    }
  };

  const handleAddFiles = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous error
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!files || files.length === 0) {
        setError(t('dashboard.training.modals.addfilesmodal.errorNoFiles'));
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('workspace_id', workspaceId);

      // Add files to FormData with logging
      files.forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await fetch(`${apiUrl}/api/v1/train/add-files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${brandvoxToken}` },
        body: formData,
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.message ||
          t('dashboard.training.modals.addfilesmodal.errorFailed')
        );
      }

      handleClose();
      await onPollState();
    } catch (err) {
      setError(err.message || t('dashboard.training.modals.addfilesmodal.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const fileInput = e.target;

    if (!fileInput.files || fileInput.files.length === 0) {
      setFiles([]);
      return;
    }

    const selectedFiles = Array.from(fileInput.files);

    const MAX_FILES = 3;
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes

    // Validate maximum number of files
    if (selectedFiles.length > MAX_FILES) {
      const errorMsg = t('dashboard.training.modals.addfilesmodal.errorTooManyFiles', { maxFiles: MAX_FILES });
      setError(errorMsg);
      return;
    }

    // Validate file size for each file
    for (let file of selectedFiles) {
      if (file.size > MAX_FILE_SIZE) {
        const errorMsg = t('dashboard.training.modals.addfilesmodal.errorFileTooBig', { fileName: file.name });
        setError(errorMsg);
        return;
      }
    }

    // Clear any previous error and set the selected files
    setError(null);
    setFiles(selectedFiles);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      centered
      size="lg"
      className="training-display-container"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{t('dashboard.training.modals.addfilesmodal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Display the error message in a dismissible Alert */}
        {error && (
          <Alert variant='danger' onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        <Form>
          <Form.Group className='mb-4'>
            <Form.Label>
              {t('dashboard.training.modals.addfilesmodal.selectFilesLabel')}
            </Form.Label>
            <Form.Control
              type='file'
              multiple
              accept='.txt,.pdf,.docx,.csv,.json,.xlsx,.xls,.xlsm,.mdx'
              onChange={handleFileChange}
              disabled={loading}
              className='file-input-control'
              style={{ display: 'block' }}
              onClick={(e) => {
                // Ensure click event propagates correctly
                if (e.target === e.currentTarget) {
                  e.target.value = null; // Reset input
                }
              }}
            />
            <Form.Text className="text-muted">
              {t('dashboard.training.modals.addfilesmodal.supportedFileTypes')}
            </Form.Text>
            <InfoBox>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <p>
                <strong>{t('dashboard.training.modals.addfilesmodal.noteTitle')}</strong> {t('dashboard.training.modals.addfilesmodal.noteText')}
              </p>
            </InfoBox>
          </Form.Group>
          {files.length > 0 && (
            <div className='selected-items-list'>
              <p>{t('dashboard.training.modals.addfilesmodal.selectedFilesLabel')}</p>
              <ul>
                {files.map((file, index) => (
                  <li key={index}>
                    {file.name} ({t('dashboard.training.modals.addfilesmodal.fileSizeMB', { size: (file.size / (1024 * 1024)).toFixed(2) })})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton className='cancel-btn' onClick={handleClose} disabled={loading}>
          {t('dashboard.training.modals.addfilesmodal.cancelButton')}
        </CancelButton>
        <PrimaryButton
          className='primary-btn'
          onClick={handleAddFiles}
          disabled={files.length === 0 || loading}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              {t('dashboard.training.modals.addfilesmodal.addingButton')}
            </>
          ) : (
            t('dashboard.training.modals.addfilesmodal.addButton')
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddFilesModal;
